import { useState, type FormEvent } from 'react';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import { enCentimes, enEuros, formaterMontant } from '../lib/format';
import type { Inscription, PersonneDetail } from '../types';
import { Champ, Modale } from './Elements';
import { PiedFormulaire } from '../pages/ReferentielsPages';

/**
 * Rembourser un trop-percu depuis la fiche adherent (2026-10-09) : un
 * virement de l'ecole vers la famille, enregistre comme un reglement
 * negatif ventile sur l'inscription choisie. Le cas sans fin anticipee —
 * une ventilation refaite qui laisse quelques euros de trop, par exemple.
 * La fin anticipee, elle, propose le remboursement dans sa propre modale.
 *
 * Seules les inscriptions de l'annee affichee qui ont recu PLUS que leur
 * du sont proposees : on ne rembourse pas ce qui n'a pas ete trop paye.
 * Le serveur plafonne de son cote a ce que l'inscription a recu.
 */
export function FormulaireRemboursement({
  ouverte,
  personne,
  anneeId,
  onFermer,
  onEnregistre,
}: {
  ouverte: boolean;
  personne: PersonneDetail;
  anneeId: string;
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const aujourdHui = new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Paris' }).format(new Date());
  const [inscriptionId, setInscriptionId] = useState('');
  const [montant, setMontant] = useState('');
  const [date, setDate] = useState(aujourdHui);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const inscriptions = useRessource(
    () =>
      ouverte
        ? api.get<Inscription[]>('/api/inscriptions', { anneeId, personneId: personne.id })
        : Promise.resolve([]),
    [ouverte, anneeId, personne.id],
  );
  const tropPayees = (inscriptions.donnees ?? []).filter((i) => i.montant_regle > i.montant_du);

  // Une seule inscription trop payee : elle est choisie d'office, et son
  // trop-percu propose. Le montant reste modifiable (remboursement partiel).
  const choisie = tropPayees.find((i) => i.id === inscriptionId) ?? (tropPayees.length === 1 ? tropPayees[0] : null);
  if (choisie && inscriptionId !== choisie.id) {
    setInscriptionId(choisie.id);
    setMontant(enEuros(choisie.montant_regle - choisie.montant_du));
  }

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    const centimes = enCentimes(montant);
    if (!choisie) {
      setErreur('Choisissez l’inscription à rembourser.');
      return;
    }
    if (centimes === null || centimes <= 0) {
      setErreur('Montant invalide : utilisez le format 120,00.');
      return;
    }
    setErreur(null);
    setEnCours(true);
    try {
      await api.post('/api/reglements/remboursements', {
        anneeId,
        inscriptionId: choisie.id,
        montant: centimes,
        dateVirement: date,
        commentaire: `Remboursement — ${choisie.activite_libelle}`,
      });
      setInscriptionId('');
      setMontant('');
      setDate(aujourdHui);
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Opération impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale titre="Rembourser un trop-perçu" ouverte={ouverte} onFermer={onFermer}>
      <form onSubmit={soumettre} className="space-y-4">
        <p className="text-sm text-encre-600">
          {personne.nom.toUpperCase()} {personne.prenom} — un virement de l’école vers la famille, enregistré
          comme un règlement négatif sur l’inscription.
        </p>

        {inscriptions.donnees && tropPayees.length === 0 ? (
          <p className="rounded-md border border-encre-200 bg-encre-50 p-3 text-sm text-encre-600">
            Aucune inscription de cette année n’a reçu plus que son dû : rien à rembourser.
          </p>
        ) : (
          <>
            <Champ libelle="Inscription trop payée">
              <select
                className="champ"
                value={choisie?.id ?? ''}
                onChange={(e) => {
                  const i = tropPayees.find((x) => x.id === e.target.value);
                  setInscriptionId(e.target.value);
                  setMontant(i ? enEuros(i.montant_regle - i.montant_du) : '');
                }}
                required
              >
                <option value="">Choisir…</option>
                {tropPayees.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.activite_libelle} — reçu {formaterMontant(i.montant_regle)} pour {formaterMontant(i.montant_du)} dû
                    (trop-perçu {formaterMontant(i.montant_regle - i.montant_du)})
                  </option>
                ))}
              </select>
            </Champ>

            <div className="grid grid-cols-2 gap-3">
              <Champ libelle="Montant remboursé (€)" aide="Proposé au trop-perçu ; ajustable.">
                <input
                  className="champ tabulaire"
                  value={montant}
                  onChange={(e) => setMontant(e.target.value)}
                  inputMode="decimal"
                  required
                />
              </Champ>
              <Champ libelle="Date du virement" aide="Future : le virement reste « prévu » jusque-là.">
                <input className="champ" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
              </Champ>
            </div>
          </>
        )}

        <PiedFormulaire erreur={erreur} enCours={enCours} onFermer={onFermer} libelle="Enregistrer le remboursement" />
      </form>
    </Modale>
  );
}
